import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from, EMPTY, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take, map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthError,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';
import { API_SCOPES, GRAPH_SCOPES } from './msal.config';
import { AuthService } from './auth.service';

/**
 * Adds access tokens to protected API requests. If the access token is expired,
 * MSAL uses the cached refresh token. If that refresh token is expired, the app
 * routes to the session-expired login page instead of attempting iframe renewal.
 * 401 retries are queued so concurrent requests share one refresh attempt.
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private msalService = inject(MsalService);
  private authService = inject(AuthService);
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<string | null>(null);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Only handle requests to our protected API
    if (!req.url.startsWith(environment.apiUrl)) {
      return next.handle(req);
    }

    const account = this.getActiveAccount();
    if (!account) {
      this.authService.redirectToSessionExpiredLogin();
      return EMPTY;
    }

    return from(
      this.acquireBackendApiToken(
        account,
        CacheLookupPolicy.AccessTokenAndRefreshToken
      )
    ).pipe(
      catchError((tokenError) => this.handleTokenError(tokenError)),
      switchMap((result) =>
        this.addAuthenticationHeaders(
          req,
          account,
          result.accessToken,
          CacheLookupPolicy.AccessTokenAndRefreshToken
        ).pipe(catchError((tokenError) => this.handleTokenError(tokenError)))
      ),
      switchMap((authReq) => {
        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401(req, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private handle401(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // If already refreshing, queue this request and wait for the new token
    if (this.isRefreshing) {
      const account = this.getActiveAccount();
      if (!account) {
        this.authService.redirectToSessionExpiredLogin();
        return EMPTY;
      }

      return this.refreshDone$.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          this.addAuthenticationHeaders(
            req,
            account,
            token as string,
            CacheLookupPolicy.AccessTokenAndRefreshToken
          ).pipe(switchMap((authReq) => next.handle(authReq)))
        ),
        catchError((tokenError) => this.handleTokenError(tokenError))
      );
    }

    const account = this.getActiveAccount();
    if (!account) {
      this.authService.redirectToSessionExpiredLogin();
      return EMPTY;
    }

    this.isRefreshing = true;
    this.refreshDone$.next(null);

    // Use the cached refresh token only; if it is expired, route to sign-in.
    return from(
      this.acquireBackendApiToken(account, CacheLookupPolicy.RefreshToken)
    ).pipe(
      switchMap((result) => {
        this.isRefreshing = false;
        this.refreshDone$.next(result.accessToken);

        return this.addAuthenticationHeaders(
          req,
          account,
          result.accessToken,
          CacheLookupPolicy.RefreshToken
        ).pipe(
          // Rethrow HTTP errors from the retried request (don't redirect for 403/500)
          switchMap((authReq) => next.handle(authReq))
        );
      }),
      catchError((tokenError) => {
        this.isRefreshing = false;
        this.refreshDone$.next(null);

        // Only route to sign-in for MSAL-specific auth failures
        if (
          tokenError instanceof InteractionRequiredAuthError ||
          tokenError instanceof AuthError
        ) {
          this.authService.redirectToSessionExpiredLogin();
          return EMPTY;
        }
        // Non-auth error — rethrow so the calling code can handle it
        return throwError(() => tokenError);
      })
    );
  }

  private getActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) return activeAccount;

    const account = this.msalService.instance.getAllAccounts()[0] ?? null;
    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }
    return account;
  }

  private acquireBackendApiToken(
    account: AccountInfo,
    cacheLookupPolicy: CacheLookupPolicy
  ) {
    return this.msalService.instance.acquireTokenSilent({
      scopes: API_SCOPES,
      account,
      cacheLookupPolicy,
    });
  }

  private acquireMicrosoftGraphToken(
    account: AccountInfo,
    cacheLookupPolicy: CacheLookupPolicy
  ): Observable<string> {
    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: GRAPH_SCOPES,
        account,
        cacheLookupPolicy,
      })
    ).pipe(map((result) => result.accessToken));
  }

  private addAuthenticationHeaders(
    req: HttpRequest<unknown>,
    account: AccountInfo,
    apiToken: string,
    graphCacheLookupPolicy: CacheLookupPolicy
  ): Observable<HttpRequest<unknown>> {
    if (!this.isAdminApiRequest(req)) {
      return of(this.addAuthorizationHeader(req, apiToken));
    }

    return this.acquireMicrosoftGraphToken(account, graphCacheLookupPolicy).pipe(
      map((graphToken) => this.addAuthorizationHeader(req, apiToken, graphToken)),
      catchError((tokenError) => this.handleTokenError(tokenError))
    );
  }

  private addAuthorizationHeader(
    req: HttpRequest<unknown>,
    accessToken: string,
    graphToken?: string
  ): HttpRequest<unknown> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };

    if (graphToken) {
      headers['X-Graph-Token'] = graphToken;
    }

    return req.clone({
      setHeaders: headers,
    });
  }

  private isAdminApiRequest(req: HttpRequest<unknown>): boolean {
    const apiBaseUrl = environment.apiUrl.replace(/\/+$/, '');
    const requestPath = req.url
      .slice(apiBaseUrl.length)
      .split('?')[0]
      .replace(/\/+$/, '');

    return requestPath === '/admin/users' || requestPath === '/admin/admins' || requestPath === '/admin/members';
  }

  private handleTokenError(error: unknown): Observable<never> {
    if (
      error instanceof InteractionRequiredAuthError ||
      error instanceof AuthError
    ) {
      this.authService.redirectToSessionExpiredLogin();
      return EMPTY;
    }

    return throwError(() => error);
  }
}
